import React, { useEffect, useState } from 'react';
import { Button, Modal, Space, Table, Tag } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import useAuthRequest from '../../../services/requests.service';
import FilterModal from './FilterModal';

const { confirm } = Modal;

const Filters = ({ setIsLoading }) => {
    const request = useAuthRequest();
    const [filters, setFilters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentFilter, setCurrentFilter] = useState(null);

    useEffect(() => {
        fetchFilters();
    }, []);

    const fetchFilters = async () => {
        try {
            const res = await request.getDataAPI('/filters');
            setFilters(res.filters);
        } catch (err) {
            toast.error('Erreur lors du chargement des filtres');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = (record) => {
        confirm({
            title: 'Supprimer ce filtre ?',
            content: 'Cette action est irréversible.',
            okText: 'Supprimer',
            okType: 'danger',
            cancelText: 'Annuler',
            centered: true,
            onOk: async () => {
                try {
                    setIsLoading(true);
                    const res = await request.deleteDataAPI(`/filters/${record._id}`);
                    setFilters(filters.filter((f) => f._id !== record._id));
                    toast.success(res.msg);
                } catch (e) {
                    toast.error(e?.msg || 'Une erreur est survenue');
                } finally {
                    setIsLoading(false);
                }
            },
        });
    };

    const handleEdit = (record) => {
        setCurrentFilter(record);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setCurrentFilter(null);
    };

    const columns = [
        {
            title: 'Nom (clé)',
            dataIndex: 'name',
            key: 'name',
            render: (text) => <code className="bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded text-sm">{text}</code>,
        },
        {
            title: 'Traduction FR',
            dataIndex: ['translations', 'fr'],
            key: 'fr',
            render: (text) => <span>{text || '—'}</span>,
        },
        {
            title: 'Position',
            dataIndex: 'position',
            key: 'position',
            sorter: (a, b) => (a.position || 0) - (b.position || 0),
            render: (val) => <span>{val ?? '—'}</span>,
        },
        {
            title: 'Statut',
            dataIndex: 'active',
            key: 'active',
            render: (val) =>
                val === 1 ? (
                    <Tag color="green">Actif</Tag>
                ) : (
                    <Tag color="default">Inactif</Tag>
                ),
        },
        {
            title: 'Actions',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button className="w-10 h-10" onClick={() => handleEdit(record)}>
                        <EditOutlined />
                    </Button>
                    <Button className="w-10 h-10" danger onClick={() => handleDelete(record)}>
                        <DeleteOutlined />
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div className="flex justify-end mb-4">
                <Button
                    size="large"
                    type="primary"
                    onClick={() => { setCurrentFilter(null); setIsModalOpen(true); }}
                    icon={
                        <svg className="w-4 h-4 fill-current" viewBox="0 0 16 16">
                            <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
                        </svg>
                    }
                >
                    <span className="ml-1">Créer un filtre</span>
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={filters}
                rowKey="_id"
                loading={loading}
                className="bg-white dark:bg-slate-800 shadow rounded-lg overflow-x-auto"
                pagination={{ pageSize: 20 }}
            />

            <FilterModal
                currentFilter={currentFilter}
                isModalOpen={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                setFilters={setFilters}
                filters={filters}
                handleClose={handleModalClose}
            />
        </div>
    );
};

export default Filters;
